import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import Input from '@/components/ui/Input';
import styles from './ClassificationDeclarations.module.scss';

interface ClassificationDeclarationsProps {
  disabled?: boolean;
  onRequiredFilled?: (allFilled: boolean) => void;
  resetTrigger?: unknown;
}

const ClassificationDeclarations = ({
  disabled = false,
  onRequiredFilled,
  resetTrigger
}: ClassificationDeclarationsProps) => {
  const { t } = useTranslation();
  
  const declarationKeys = ['declaration1', 'declaration2', 'declaration3'] as const;
  
  const [declarations, setDeclarations] = useState({
    declaration1: false,
    declaration2: false,
    declaration3: false,
  });

  const [requesterName, setRequesterName] = useState('');

  const allDeclarationsChecked = 
    declarations.declaration1 &&
    declarations.declaration2 &&
    declarations.declaration3;

  const allRequiredFilled = allDeclarationsChecked && requesterName.trim().length > 1;

  // Reset state when resetTrigger changes
  useEffect(() => {
    setDeclarations({
      declaration1: false,
      declaration2: false,
      declaration3: false,
    });
    setRequesterName('');
  }, [resetTrigger]);

  // Notify parent when required fields status changes
  useEffect(() => {
    onRequiredFilled?.(allRequiredFilled);
  }, [allRequiredFilled, onRequiredFilled]);

  const handleDeclarationChange = (declarationKey: keyof typeof declarations, checked: boolean) => {
    setDeclarations((prev) => ({
      ...prev,
      [declarationKey]: checked,
    }));
  };

  return (
    <div className={styles.content}>
      <div className={styles.declarationsSection}>
        <p className="body-lr-regular text-(--font-sub-title)">{t('toSubmitRequestConfirm')}</p>

        <div className={styles.declarationsList}>
          {declarationKeys.map((key) => (
            // The whole row toggles the declaration - the bare 16px checkbox was too
            // small a target and users kept clicking the (previously dead) text.
            // Checkbox stops propagation internally, so clicking it directly won't
            // double-toggle through this row handler.
            <div
              key={key}
              className={styles.declarationRow}
              onClick={() => {
                if (!disabled) handleDeclarationChange(key, !declarations[key]);
              }}
            >
              <Checkbox
                checked={declarations[key]}
                onCheckedChange={(checked) => handleDeclarationChange(key, checked)}
                disabled={disabled}
              />
              <p className={styles.declarationText}>{t(key)}</p>
            </div>
          ))}
        </div>

        <div className={styles.nameInputSection}>
          <label className="body-lr-semibold color-(--font-sub-title)" htmlFor="requesterName">
            {t('declarantName')} <span className={styles.required}>*</span>
          </label>
          <Input
            id="requesterName"
            type="text"
            value={requesterName}
            onChange={(e) => setRequesterName(e.target.value)}
            placeholder={t('declarantName')}
            className="w-full"
            disabled={disabled}
            required
          />
        </div>
      </div>
    </div>
  );
};

export default ClassificationDeclarations;

